
import { Control, useWatch, UseFormSetValue } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CharacterFormData } from "../validation/characterSchema";
import { CharacterFieldSelect } from "../components/CharacterFieldSelect";
import { generateCharacterSlug } from "@/utils/characterSlug";
import { useCharacterSpecies } from "@/hooks/useCharacterSpecies";
import { useCharacterRoles } from "@/hooks/useCharacterRoles";

interface BasicInformationSectionProps {
  control: Control<CharacterFormData>;
  setValue: UseFormSetValue<CharacterFormData>;
}

export const BasicInformationSection = ({ control, setValue }: BasicInformationSectionProps) => {
  const watchName = useWatch({ control, name: "name" });
  const { species, isLoading: speciesLoading } = useCharacterSpecies();
  const { roles, isLoading: rolesLoading } = useCharacterRoles();

  const activeSpecies = species.filter(s => s.is_active).map(s => ({ 
    value: s.name, 
    label: s.name 
  }));
  
  const activeRoles = roles.filter(r => r.is_active).map(r => ({ 
    value: r.name, 
    label: r.name 
  }));

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold border-b pb-2">Basic Information</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FormField
          control={control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Character Name *</FormLabel>
              <FormControl>
                <Input 
                  placeholder="Enter character name" 
                  {...field}
                  onChange={(e) => {
                    field.onChange(e);
                    // Auto-generate slug when name changes and slug is empty
                    const currentSlug = control._formState.defaultValues?.slug;
                    if (!currentSlug) {
                      const newSlug = generateCharacterSlug(e.target.value);
                      setValue("slug", newSlug);
                    }
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={control}
          name="slug"
          render={({ field }) => (
            <FormItem>
              <FormLabel>URL Slug *</FormLabel>
              <FormControl>
                <Input 
                  placeholder="character-slug" 
                  {...field}
                />
              </FormControl>
              <FormMessage />
              <p className="text-xs text-gray-500">
                Used in URL: /characters/{field.value || "character-slug"}
              </p>
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <CharacterFieldSelect
          control={control}
          name="species"
          label="Species"
          placeholder="Select species"
          options={activeSpecies}
          isLoading={speciesLoading}
        />

        <CharacterFieldSelect
          control={control}
          name="role"
          label="Role"
          placeholder="Select role"
          options={activeRoles}
          isLoading={rolesLoading}
        />
      </div>

      <FormField
        control={control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Enter character description"
                className="min-h-[80px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="personality"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Personality</FormLabel>
            <FormControl>
              <Textarea
                placeholder="Describe the character's personality"
                className="min-h-[60px]"
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
